// Dependency-free reference normalization shared by contracts and Convex.
// This module is an explicit committed deployment/digest input; it must not
// import schema, UI, or runtime libraries into the authority's bundle.

/** Read compatibility for persisted pre-Workroom records. New writes and public
 * schemas use workroom/workroomKey/workrooms. Never edit append-only history. */
export function canonicalWorkroomKey(key: string): string {
  return key === "skill-rooms" ? "workrooms" : key;
}
// Admitted keys already use bounded, trimmed input semantics. Resolve aliases
// after that trimming, without changing any historical read value.
const canonicalInputWorkroomKey = (value: unknown): unknown =>
  typeof value === "string" ? canonicalWorkroomKey(value.trim()) : value;
export function canonicalRoomKind(kind: unknown): unknown {
  return kind === "skill" ? "workroom" : kind;
}
export function normalizeWorkroomReference<T>(value: T): T {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return value;
  const source = value as Record<string, unknown>;
  const { skillRoomKey: legacyKey, ...rest } = source;
  const result = { ...rest };
  if (source.workroomKey !== undefined || legacyKey !== undefined) {
    const key = source.workroomKey !== undefined ? source.workroomKey : legacyKey;
    result.workroomKey = typeof key === "string" ? canonicalWorkroomKey(key) : key;
  }
  if (source.kind === "skill") result.kind = "workroom";
  // Only the old builtin default label changes in its derived view; authored
  // labels and the original stored room/move remain exact.
  if (source.label !== undefined)
    result.label = canonicalWorkroomLabel(result.workroomKey, source.label);
  return result as T;
}

/** Input-only ambiguity check. Historical decoders preserve an explicit canonical
 * value; admitted new requests must not name two different Workrooms. */
export function hasConflictingWorkroomKeys(value: unknown): boolean {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const fields = value as Record<string, unknown>;
  if (fields.workroomKey === undefined || fields.skillRoomKey === undefined) return false;
  return (
    canonicalInputWorkroomKey(fields.workroomKey) !== canonicalInputWorkroomKey(fields.skillRoomKey)
  );
}

/** Default management labels are display aliases only. Authored labels stay exact. */
export function canonicalWorkroomLabel(key: unknown, label: unknown): unknown {
  return typeof key === "string" &&
    canonicalWorkroomKey(key) === "workrooms" &&
    ["Skill Rooms", "Skill Boards", "Workrooms"].includes(String(label))
    ? "Workrooms"
    : label;
}
/** Command labels are facts supplied by the caller, not display text to rewrite. */
export function normalizeWorkroomInputReference<T>(value: T): T {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return normalizeWorkroomReference(value);
  const input = { ...value } as Record<string, unknown>;
  for (const field of ["workroomKey", "skillRoomKey"]) {
    if (Object.hasOwn(input, field)) input[field] = canonicalInputWorkroomKey(input[field]);
  }
  const normalized = normalizeWorkroomReference(input);
  if (Object.hasOwn(value, "label"))
    (normalized as Record<string, unknown>).label = (value as Record<string, unknown>).label;
  return normalized as T;
}
